import {Component, ElementRef, HostListener, OnDestroy, ViewChild} from '@angular/core';
import {AsyncPipe, NgIf} from '@angular/common';
import {Store} from '@ngrx/store';
import {Subscription} from 'rxjs';
import {ContentScope} from '@shared/commands/command-base';
import {GraphqlContentScope} from '@shared/models/graphql-content-scope.model';
import {SetCurrentRightSideComponentAction} from '@shared/components/platform-sidebar/store/platform-sidebar.action';
import {GetConversationChatAction} from '@features/task-manager/collaboration/store/collaboration.action';
import {CollaborationConfig} from '@features/task-manager/collaboration/store/collaboration.config';
import {
  ConversationComponentName,
  ConversationListComponentName
} from '@features/task-manager/collaboration/store/collaboration.constants';
import {CollaborationQuery} from '@features/task-manager/collaboration/store/collaboration.query';
import {CallService, CallState} from '../call.service';
import {CallRinger} from '../call-ringer';
import {CallParticipants} from '../call-participants';

/**
 * The incoming-call overlay (ITP-E-170-01): who is calling, Decline and Accept (Decline, ITP-E-170-02). It
 * closes on every terminal state of the ring. Mounted once, beside the router outlet.
 *
 * Every open tab shows it and rings (ITP-E-170-05) — the ringtone, and a desktop notification while the tab is
 * hidden (CallRinger) — and it closes when the call is accepted or declined in another tab.
 *
 * A call started with the Video call button rings as video (ITP-E-182-05): the overlay says "Incoming video call", with
 * a camera, and Accept answers it with the camera — the video call card opens and this side's camera starts.
 *
 * It is carbon's incoming-call card (ITP-E-191-01, orbitax-carbon `skills/call-overlay.md`): a centred card over a
 * backdrop that never declines on a click; the kind of call, the caller's face with a ringing pulse, the name, and the
 * conversation the call comes from. A video call adds Audio only — the same accept, without the camera. Reply with
 * message declines and opens that conversation in the right sidebar with its composer focused. It is an alert dialog
 * to assistive technology: Accept takes the focus, Tab stays in the card, Esc declines, and the focus goes back where
 * it was when the card closes.
 */
@Component({
  selector: 'app-incoming-call',
  standalone: true,
  imports: [NgIf, AsyncPipe],
  templateUrl: './incoming-call.component.html',
  styleUrls: ['./incoming-call.component.scss']
})
export class IncomingCallComponent implements OnDestroy {
  readonly state$ = this.callService.state$;
  private readonly ring: Subscription;
  private focusBefore: HTMLElement | null = null;

  @ViewChild('card') card?: ElementRef<HTMLElement>;

  /** Accept takes the focus as the overlay appears; the focus returns where it was once it closes. */
  @ViewChild('acceptButton')
  set acceptButton(button: ElementRef<HTMLButtonElement> | undefined) {
    if (button) {
      this.focusBefore = document.activeElement as HTMLElement;
      button.nativeElement.focus();
    } else if (this.focusBefore) {
      this.focusBefore.focus?.();
      this.focusBefore = null;
    }
  }

  constructor(private callService: CallService,
              private ringer: CallRinger,
              private store: Store<any>) {
    this.ring = this.state$.subscribe(state => this.onState(state));
  }

  /**
   * The line under the name: the group conversation and how many are in it ("Tax team · 5 members"), or "Direct call".
   * The count is the server's member count, else the call's participants; with neither, the conversation alone. Never
   * the caller's place or status.
   */
  static context(state: CallState): string {
    if (!state.isGroup) {
      return 'Direct call';
    }
    const title = state.title || 'Group call';
    const count = state.memberCount ?? (state.participants?.length || undefined);
    return count ? `${title} · ${count} ${count === 1 ? 'member' : 'members'}` : title;
  }

  /** Carbon's avatar pair for a name, by its first letter (`CallParticipants.avatarTone`). */
  static avatarTone(name: string | undefined): string {
    return CallParticipants.avatarTone(name);
  }

  contextOf(state: CallState): string {
    return IncomingCallComponent.context(state);
  }

  initialsOf(name: string | undefined): string {
    return CallParticipants.initials(name || '');
  }

  toneOf(name: string | undefined): string {
    return IncomingCallComponent.avatarTone(name);
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: KeyboardEvent): void {
    if (this.callService.state.phase === 'incoming' && !event.defaultPrevented) {
      event.preventDefault();
      this.onDecline();
    }
  }

  /** Tab and Shift+Tab go round the card's buttons and never leave it while it rings. */
  onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab' || !this.card) {
      return;
    }
    const buttons = Array.from(this.card.nativeElement.querySelectorAll<HTMLElement>('button:not([disabled])'));
    if (!buttons.length) {
      return;
    }
    const first = buttons[0];
    const last = buttons[buttons.length - 1];
    const inside = buttons.includes(document.activeElement as HTMLElement);
    if (event.shiftKey && (document.activeElement === first || !inside)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !inside)) {
      event.preventDefault();
      first.focus();
    }
  }

  onAccept(): void {
    this.ringer.requestNotificationPermission();
    this.callService.accept().then();
  }

  /** Audio only: the video call is answered as any call is, only this side's camera stays off. */
  onAudioOnly(): void {
    this.ringer.requestNotificationPermission();
    this.callService.accept(false).then();
  }

  onDecline(): void {
    this.ringer.requestNotificationPermission();
    this.callService.decline().then();
  }

  /**
   * Reply with message: declines, then opens the call's conversation in the right sidebar the way the chat list opens
   * one — its ConversationComponent focuses the composer. A ring whose conversation is not known opens the chat list.
   */
  onReply(): void {
    this.ringer.requestNotificationPermission();
    const conversationId = this.callService.replyWithMessage();
    if (conversationId) {
      this.store.dispatch(new GetConversationChatAction(CollaborationConfig.CollaborationApiKey,
        CollaborationQuery.getChatConversationById(conversationId, GraphqlContentScope.getScope(ContentScope.Company)),
        ConversationComponentName));
    } else {
      this.store.dispatch(new SetCurrentRightSideComponentAction({component: ConversationListComponentName, data: {}, componentInfo: null}));
    }
  }

  ngOnDestroy(): void {
    this.ring.unsubscribe();
    this.ringer.stop();
  }

  private onState(state: CallState): void {
    if (state.phase === 'incoming' && state.invitationId) {
      this.ringer.ring(state.invitationId, state.peerName, state.mode === 'video');
    } else {
      this.ringer.stop();
    }
  }
}
